/* ProductAI Cova storefront instrumentation V1 — cart event reliability fix.
 * Shopify target: assets/productai-funnel-v1.js
 * Scope: product_viewed and product_added_to_cart only.
 */
(() => {
  "use strict";

  if (window.__productAiFunnelV1Loaded) return;
  window.__productAiFunnelV1Loaded = true;

  const script = document.currentScript;
  const endpoint = script?.dataset.collectorEndpoint;
  const storePublicId = script?.dataset.storePublicId;
  const currency = script?.dataset.currency || undefined;
  const productId = script?.dataset.productId || undefined;
  const variantId = script?.dataset.variantId || undefined;
  const amount = script?.dataset.amount || undefined;
  const allowedEvents = new Set(["product_viewed", "product_added_to_cart"]);

  if (!endpoint || !storePublicId) return;

  const numericId = value => {
    const text = String(value || "");
    if (/^[1-9]\d*$/.test(text)) return text;
    return text.match(/^gid:\/\/shopify\/(?:Product|ProductVariant)\/([1-9]\d*)$/)?.[1];
  };

  const gid = (kind, value) => {
    const numeric = numericId(value);
    return numeric ? `gid://shopify/${kind}/${numeric}` : undefined;
  };

  const token = () => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  };

  const compact = value => Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== undefined && field !== null && field !== "")
  );

  const send = (eventType, fields = {}) => {
    if (!allowedEvents.has(eventType)) return;
    const payload = compact({
      schema_version: "cova-custom-pixel-v1",
      event_type: eventType,
      event_timestamp: new Date().toISOString(),
      store_public_id: storePublicId,
      dedup_token: token(),
      ...fields,
    });
    void fetch(endpoint, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  };

  if (productId) {
    send("product_viewed", compact({
      product_gid: gid("Product", productId),
      variant_gid: gid("ProductVariant", variantId),
      currency,
      amount,
    }));
  }

  document.addEventListener("cart:update", event => {
    const detail = event.detail;
    if (!detail || detail.data?.source !== "product-form-component" || detail.data?.didError === true) return;

    const eventProductId = numericId(detail.data?.productId);
    const explicitVariantId = numericId(detail.data?.variantId) || numericId(detail.sourceId);
    const items = Array.isArray(detail.resource?.items) ? detail.resource.items : [];
    const productItems = eventProductId
      ? items.filter(item => numericId(item.product_id) === eventProductId)
      : [];
    const item = explicitVariantId
      ? items.find(candidate => numericId(candidate.variant_id ?? candidate.id) === explicitVariantId)
      : productItems.length === 1 ? productItems[0] : undefined;
    const resolvedVariantId = explicitVariantId || numericId(item?.variant_id ?? item?.id);
    if (!resolvedVariantId) return;

    const cents = item?.final_price ?? item?.price;
    send("product_added_to_cart", compact({
      product_gid: gid("Product", eventProductId ?? item?.product_id),
      variant_gid: gid("ProductVariant", resolvedVariantId),
      quantity: Number(detail.data?.itemCount) || 1,
      currency,
      amount: Number.isFinite(Number(cents)) ? (Number(cents) / 100).toFixed(2) : undefined,
    }));
  }, { capture: true });
})();
